/**
 * DevenirPartenaireForm.js
 * 
 * 🌐 FORMULAIRE BILINGUE "DEVENIR PARTENAIRE" 🌐
 * Design ultra-moderne et professionnel
 * 
 * Ce formulaire est accessible depuis le Footer du site
 * Les demandes sont reçues dans le Dashboard Super Admin
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent } from './ui/dialog';
import { Handshake, CheckCircle, X, AlertCircle, Loader2, Globe, Building2, Mail, User, FileText, Sparkles, Shield, Users, ArrowRight } from 'lucide-react';

// Configuration centralisée
import { INDICATIFS_TELEPHONIQUES, STATUTS_JURIDIQUES, PAYS_LIST, getIndicatifByCode, getPaysByCode, getFlagUrl, validatePhone } from '../config/membreFormConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Traductions intégrées pour le formulaire
const translations = {
  fr: {
    title: 'Devenir Partenaire',
    subtitle: 'Rejoignez le réseau O\'SCOP OUTREMER',
    description: 'Vous souhaitez devenir partenaire de notre réseau d\'économie sociale et solidaire ? Remplissez ce formulaire et notre équipe vous contactera dans les plus brefs délais.',
    benefits_title: 'Avantages du partenariat',
    benefit_1: 'Accès au réseau ESS des Outre-mer',
    benefit_2: 'Accompagnement personnalisé',
    benefit_3: 'Visibilité auprès de nos membres',
    benefit_4: 'Participation aux événements exclusifs',
    form_section_identity: 'Vos informations',
    form_section_company: 'Votre entreprise',
    form_section_message: 'Votre demande de partenariat',
    field_lastname: 'Nom',
    field_firstname: 'Prénom',
    field_email: 'Email professionnel',
    field_phone: 'Téléphone',
    field_company: 'Raison sociale',
    field_siret: 'N° SIRET',
    field_status: 'Statut juridique',
    field_country: 'Pays',
    field_message_title: 'Décrivez votre projet de partenariat',
    field_message_placeholder: 'Présentez votre entreprise, vos activités et comment vous envisagez ce partenariat avec O\'SCOP OUTREMER...\n\nN\'hésitez pas à détailler :\n- Votre secteur d\'activité\n- Vos motivations\n- Les synergies possibles',
    field_message_help: 'Minimum 50 caractères',
    validation_required: 'Ce champ est obligatoire',
    validation_email: 'Email invalide',
    validation_phone: 'Numéro invalide',
    validation_siret: '14 chiffres requis',
    validation_message_min: 'Minimum 50 caractères requis',
    phone_valid: 'Numéro valide',
    phone_missing: 'chiffre(s) manquant(s)',
    phone_extra: 'chiffre(s) en trop',
    btn_submit: 'Envoyer ma demande de partenariat',
    btn_submitting: 'Envoi en cours...',
    btn_close: 'Fermer',
    success_title: 'Demande envoyée !',
    success_message: 'Votre demande de partenariat a été transmise à notre équipe. Nous vous contacterons dans les 48 heures.',
    error_generic: 'Une erreur est survenue. Veuillez réessayer.',
    chars_count: 'caractères'
  },
  en: {
    title: 'Become a Partner',
    subtitle: 'Join the O\'SCOP OUTREMER network',
    description: 'Would you like to become a partner of our social and solidarity economy network? Fill out this form and our team will contact you as soon as possible.',
    benefits_title: 'Partnership benefits',
    benefit_1: 'Access to the Overseas ESS network',
    benefit_2: 'Personalized support',
    benefit_3: 'Visibility among our members',
    benefit_4: 'Participation in exclusive events',
    form_section_identity: 'Your information',
    form_section_company: 'Your company',
    form_section_message: 'Your partnership request',
    field_lastname: 'Last name',
    field_firstname: 'First name',
    field_email: 'Professional email',
    field_phone: 'Phone',
    field_company: 'Company name',
    field_siret: 'SIRET number',
    field_status: 'Legal status',
    field_country: 'Country',
    field_message_title: 'Describe your partnership project',
    field_message_placeholder: 'Present your company, your activities and how you envision this partnership with O\'SCOP OUTREMER...\n\nFeel free to detail:\n- Your business sector\n- Your motivations\n- Possible synergies',
    field_message_help: 'Minimum 50 characters',
    validation_required: 'This field is required',
    validation_email: 'Invalid email',
    validation_phone: 'Invalid number',
    validation_siret: '14 digits required',
    validation_message_min: 'Minimum 50 characters required',
    phone_valid: 'Valid number',
    phone_missing: 'missing digit(s)',
    phone_extra: 'extra digit(s)',
    btn_submit: 'Send my partnership request',
    btn_submitting: 'Sending...',
    btn_close: 'Close',
    success_title: 'Request sent!',
    success_message: 'Your partnership request has been sent to our team. We will contact you within 48 hours.',
    error_generic: 'An error occurred. Please try again.',
    chars_count: 'characters'
  }
};

export default function DevenirPartenaireForm({ open, onOpenChange }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = translations[currentLang];

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    indicatif_pays: '+33',
    raison_sociale: '',
    siret: '',
    statut_juridique: '',
    pays: 'FR',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Validation téléphone
  const selectedIndicatif = getIndicatifByCode(formData.indicatif_pays || '+33');
  const phoneValidation = validatePhone(formData.telephone || '', formData.indicatif_pays || '+33');

  const validateForm = () => {
    const errors = {};

    if (!formData.nom?.trim()) errors.nom = t.validation_required;
    if (!formData.prenom?.trim()) errors.prenom = t.validation_required;
    if (!formData.email?.trim()) errors.email = t.validation_required;
    if (!formData.telephone?.trim()) errors.telephone = t.validation_required;
    if (!formData.raison_sociale?.trim()) errors.raison_sociale = t.validation_required;
    if (!formData.message?.trim()) errors.message = t.validation_required;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = t.validation_email;
    }

    // Phone validation
    if (formData.telephone && !phoneValidation.isValid) {
      errors.telephone = t.validation_phone;
    }

    // SIRET validation (optional but if filled, must be 14 digits)
    if (formData.siret && formData.siret.replace(/\s/g, '').length !== 14) {
      errors.siret = t.validation_siret;
    }

    // Message minimum length
    if (formData.message && formData.message.trim().length < 50) {
      errors.message = t.validation_message_min;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(currentLang === 'fr' ? 'Veuillez corriger les erreurs' : 'Please fix the errors');
      return;
    }

    setSubmitting(true);
    try {
      const telephoneComplet = `${formData.indicatif_pays} ${formData.telephone}`;

      await axios.post(`${API}/demandes-partenariat`, {
        ...formData,
        telephone: telephoneComplet,
        type: 'partenariat',
        langue: currentLang,
        date_demande: new Date().toISOString()
      });

      setShowSuccess(true);
      resetForm();

      // Close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onOpenChange(false);
      }, 4000);

    } catch (error) {
      toast.error(error.response?.data?.detail || t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      indicatif_pays: '+33',
      raison_sociale: '',
      siret: '',
      statut_juridique: '',
      pays: 'FR',
      message: ''
    });
    setFormErrors({});
  };

  const handleClose = () => {
    resetForm();
    setShowSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0 gap-0">
        {/* Header avec dégradé moderne */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          {/* Badge de langue */}
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full text-sm">
            <Globe className="h-4 w-4" />
            <span>{currentLang === 'fr' ? 'Français' : 'English'}</span>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Handshake className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">{t.title}</h2>
              <p className="text-white/80 text-lg">{t.subtitle}</p>
            </div>
          </div>

          <p className="mt-4 text-white/90 max-w-2xl">
            {t.description}
          </p>

          {/* Avantages en badges */}
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full text-sm">
              <Shield className="h-4 w-4" />
              {t.benefit_1}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full text-sm">
              <Users className="h-4 w-4" />
              {t.benefit_2}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full text-sm">
              <Sparkles className="h-4 w-4" />
              {t.benefit_3}
            </span>
          </div>
        </div>

        {/* Contenu du formulaire */}
        {showSuccess ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.success_title}</h3>
            <p className="text-gray-600 max-w-md mx-auto">{t.success_message}</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Section Identité */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-indigo-600 font-semibold">
                <User className="h-5 w-5" />
                {t.form_section_identity}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nom" className="text-gray-700">{t.field_lastname} *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className={`mt-1 ${formErrors.nom ? 'border-red-500' : ''}`}
                  />
                  {formErrors.nom && <p className="text-xs text-red-500 mt-1">{formErrors.nom}</p>}
                </div>
                <div>
                  <Label htmlFor="prenom" className="text-gray-700">{t.field_firstname} *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className={`mt-1 ${formErrors.prenom ? 'border-red-500' : ''}`}
                  />
                  {formErrors.prenom && <p className="text-xs text-red-500 mt-1">{formErrors.prenom}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-gray-700">{t.field_email} *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`pl-10 ${formErrors.email ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                </div>

                {/* Téléphone avec indicatif */}
                <div>
                  <Label htmlFor="telephone" className="text-gray-700">{t.field_phone} *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={formData.indicatif_pays || '+33'}
                      onValueChange={(value) => setFormData({ ...formData, indicatif_pays: value, telephone: '' })}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue>
                          <span className="flex items-center gap-2">
                            <img
                              src={getFlagUrl(selectedIndicatif.flag)}
                              alt={selectedIndicatif.name}
                              className="w-5 h-4 object-cover rounded-sm"
                            />
                            <span className="text-sm">{selectedIndicatif.code}</span>
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-[250px]">
                        {INDICATIFS_TELEPHONIQUES.map((ind, idx) => (
                          <SelectItem key={`${ind.code}-${idx}`} value={ind.code}>
                            <span className="flex items-center gap-2">
                              <img src={getFlagUrl(ind.flag)} alt={ind.name} className="w-5 h-4 object-cover rounded-sm" />
                              <span>{ind.code} {ind.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex-1 relative">
                      <Input
                        id="telephone"
                        value={formData.telephone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d\s]/g, '');
                          setFormData({ ...formData, telephone: value });
                        }}
                        className={`${formErrors.telephone ? 'border-red-500' : phoneValidation.isValid && phoneValidation.currentLength > 0 ? 'border-green-500' : ''}`}
                        placeholder={`${phoneValidation.expectedLength} chiffres`}
                      />
                      {phoneValidation.currentLength > 0 && (
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${phoneValidation.isValid ? 'text-green-600' : 'text-red-500'}`}>
                          {phoneValidation.currentLength}/{phoneValidation.expectedLength}
                        </span>
                      )}
                    </div>
                  </div>
                  {phoneValidation.currentLength > 0 && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${phoneValidation.isValid ? 'text-green-600' : 'text-orange-500'}`}>
                      {phoneValidation.isValid ? (
                        <><CheckCircle className="h-3 w-3" /> {t.phone_valid}</>
                      ) : (
                        <><AlertCircle className="h-3 w-3" /> {Math.abs(phoneValidation.currentLength - phoneValidation.expectedLength)} {phoneValidation.currentLength < phoneValidation.expectedLength ? t.phone_missing : t.phone_extra}</>
                      )}
                    </p>
                  )}
                  {formErrors.telephone && <p className="text-xs text-red-500 mt-1">{formErrors.telephone}</p>}
                </div>
              </div>
            </div>

            {/* Section Entreprise */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-purple-600 font-semibold">
                <Building2 className="h-5 w-5" />
                {t.form_section_company}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="raison_sociale" className="text-gray-700">{t.field_company} *</Label>
                  <Input
                    id="raison_sociale"
                    value={formData.raison_sociale}
                    onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                    className={`mt-1 ${formErrors.raison_sociale ? 'border-red-500' : ''}`}
                  />
                  {formErrors.raison_sociale && <p className="text-xs text-red-500 mt-1">{formErrors.raison_sociale}</p>}
                </div>

                <div>
                  <Label htmlFor="siret" className="text-gray-700">{t.field_siret}</Label>
                  <Input
                    id="siret"
                    value={formData.siret}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d]/g, '').slice(0, 14);
                      setFormData({ ...formData, siret: value });
                    }}
                    placeholder="12345678901234"
                    maxLength={14}
                    className={`mt-1 font-mono ${formErrors.siret ? 'border-red-500' : formData.siret.length === 14 ? 'border-green-500' : ''}`}
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">14 {t.chars_count}</p>
                    {formData.siret && (
                      <p className={`text-xs ${formData.siret.length === 14 ? 'text-green-600' : 'text-orange-500'}`}>
                        {formData.siret.length}/14
                      </p>
                    )}
                  </div>
                  {formErrors.siret && <p className="text-xs text-red-500">{formErrors.siret}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="statut_juridique" className="text-gray-700">{t.field_status}</Label>
                  <Select
                    value={formData.statut_juridique}
                    onValueChange={(value) => setFormData({ ...formData, statut_juridique: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUTS_JURIDIQUES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="pays" className="text-gray-700">{t.field_country}</Label>
                  <Select value={formData.pays} onValueChange={(value) => setFormData({ ...formData, pays: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue>
                        {(() => {
                          const pays = getPaysByCode(formData.pays);
                          return (
                            <span className="flex items-center gap-2">
                              <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                              <span>{pays.name}</span>
                            </span>
                          );
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PAYS_LIST.map((pays) => (
                        <SelectItem key={pays.code} value={pays.code}>
                          <span className="flex items-center gap-2">
                            <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                            <span>{pays.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section Message de partenariat */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-pink-600 font-semibold">
                <FileText className="h-5 w-5" />
                {t.form_section_message}
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-5 rounded-xl border border-indigo-100">
                <Label htmlFor="message" className="text-gray-700 font-medium flex items-center gap-2 flex-wrap">
                  <span>{t.field_message_title} *</span>
                  {/* V37.11 — Badge rouge "50 caractères obligatoires" */}
                  <span
                    data-testid="message-min-chars-hint"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    50 caractères obligatoires
                  </span>
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t.field_message_placeholder}
                  rows={6}
                  className={`mt-2 bg-white resize-none ${formErrors.message ? 'border-red-500' : formData.message.length >= 50 ? 'border-green-500' : ''}`}
                />
                {/* V37.11 — Mini progress bar (objectif 50 caractères) */}
                {(() => {
                  const len = formData.message.length;
                  const pct = Math.min(100, Math.round((len / 50) * 100));
                  const reached = len >= 50;
                  return (
                    <div className="mt-2" data-testid="message-progress-bar">
                      <div className="relative w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out ${reached ? 'bg-gradient-to-r from-emerald-500 to-green-500' : 'bg-gradient-to-r from-red-500 to-orange-500'}`}
                          style={{ width: `${pct}%` }}
                          data-testid="message-progress-fill"
                          data-reached={reached ? 'true' : 'false'}
                        />
                      </div>
                    </div>
                  );
                })()}
                <div className="flex justify-between mt-2">
                  <p className="text-xs text-gray-500">{t.field_message_help}</p>
                  <p className={`text-xs font-medium ${formData.message.length >= 50 ? 'text-green-600' : 'text-orange-500'}`}>
                    {formData.message.length} {t.chars_count}
                  </p>
                </div>
                {formErrors.message && <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>}
              </div>
            </div>

            {/* Bouton de soumission */}
            <div className="pt-4 border-t">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-600 text-white font-semibold text-lg shadow-lg shadow-purple-500/30"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t.btn_submitting}
                  </>
                ) : (
                  <>
                    <Handshake className="h-5 w-5 mr-2" />
                    {t.btn_submit}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
